import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Moon, Sun, LogOut, BookOpen, User, StickyNote, Upload, BarChart3 } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "./ui/dropdown-menu";
import UploadDialog from "./UploadDialog";

export default function Header({ onOpenAuth }) {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [uploadOpen, setUploadOpen] = useState(false);

  const openUpload = () => {
    if (!user) { onOpenAuth(); return; }
    setUploadOpen(true);
  };

  return (
    <header className="glass-header sticky top-0 z-40 border-b border-border">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-10 h-16">
        <Link to="/" className="flex items-baseline gap-2" data-testid="logo-link">
          <span className="font-display text-2xl md:text-3xl font-semibold tracking-tight lowercase">
            pagesbyadam
          </span>
          <span className="font-ui text-[10px] tracking-caps text-muted-foreground hidden sm:inline">
            a reader&apos;s library
          </span>
        </Link>

        <div className="flex items-center gap-2">
          <button
            onClick={openUpload}
            data-testid="header-upload-btn"
            className="hidden md:inline-flex items-center gap-1.5 font-ui text-xs tracking-caps px-3 py-1.5 rounded-full hover:bg-secondary transition-colors text-muted-foreground"
            title="upload your own book"
          >
            <Upload className="w-3.5 h-3.5" /> upload
          </button>

          <button
            onClick={toggle}
            data-testid="theme-toggle-btn"
            className="p-2 rounded-full hover:bg-secondary transition-colors"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-btn"
                  className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full hover:bg-secondary transition-colors"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground grid place-items-center font-ui text-xs font-medium">
                      {(user.display_name || user.email)[0].toUpperCase()}
                    </div>
                  )}
                  <span className="font-ui text-sm hidden sm:inline">{user.display_name || user.email}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 font-ui">
                <DropdownMenuItem onClick={() => navigate("/shelf")} data-testid="my-shelf-menu-item">
                  <BookOpen className="w-4 h-4 mr-2" /> my shelf
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/stats")} data-testid="stats-menu-item">
                  <BarChart3 className="w-4 h-4 mr-2" /> reading stats
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/notes")} data-testid="notepad-menu-item">
                  <StickyNote className="w-4 h-4 mr-2" /> notepad
                </DropdownMenuItem>
                <DropdownMenuItem onClick={openUpload} data-testid="upload-menu-item">
                  <Upload className="w-4 h-4 mr-2" /> upload a book
                </DropdownMenuItem>
                <DropdownMenuItem disabled>
                  <User className="w-4 h-4 mr-2" /> {user.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-menu-item">
                  <LogOut className="w-4 h-4 mr-2" /> sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={onOpenAuth}
              data-testid="open-auth-btn"
              className="rounded-full font-ui text-sm px-5"
            >
              sign in
            </Button>
          )}
        </div>
      </div>

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} />
    </header>
  );
}
